import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : null),
    [article]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
        : [],
    [article]
  );

  const getCategoryName = (catSlug: string) => {
    const cat = categories.find((c) => c.slug === catSlug);
    return cat ? cat.name : catSlug;
  };

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-20 h-20 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
                文章未找到
              </h1>
              <p className="text-foreground-600 mb-6">
                该文章不存在或已被移除
              </p>
              <Link
                to="/"
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors inline-block whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    url: `https://www.nantongwangzhan.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '男同网站',
    },
    image: article.coverImage,
  };

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${article.title} - 男同网站`,
    url: `https://www.nantongwangzhan.com/article/${article.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '男同网站',
      url: 'https://www.nantongwangzhan.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article>
            <header className="bg-background-100 border-b border-background-200/70">
              <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: category?.name || '', href: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
                <div className="mt-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4 hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {category?.name}
                  </Link>
                </div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-foreground-600 text-base leading-relaxed mb-5">
                  {article.summary}
                </p>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>
            </header>

            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div
                className="prose prose-lg max-w-none
                  prose-headings:text-foreground-950 prose-headings:font-heading prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-950"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">标签：</span>
                  <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                    LGBTQ+
                  </span>
                  <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                    {category?.name}
                  </span>
                  <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                    男同社区
                  </span>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14 md:py-16">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard
                      key={ra.id}
                      id={ra.id}
                      title={ra.title}
                      slug={ra.slug}
                      summary={ra.summary}
                      category={ra.category}
                      categoryName={getCategoryName(ra.category)}
                      coverImage={ra.coverImage}
                      author={ra.author}
                      publishedAt={ra.publishedAt}
                      readTime={ra.readTime}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}